// File: src/pages/CaseDetailPage.js
// Version: 2.1 (2025-10-24)
// Description: Страница деталей дела с разделенной логикой загрузки и исправленной ошибкой Rules of Hooks.
// Изменения v2.1:
// - ИСПРАВЛЕНА ОШИБКА "Rules of Hooks": useMemo в AssignmentHistory
//   перемещен ДО условного return (if (isLoading)).
// - Добавлен usePermissions для корректной проверки прав.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import apiService from '../services/apiService';
import LoadingSpinner from '../components/LoadingSpinner';
import './CaseDetailPage.css';
import { usePermissions } from '../hooks/usePermissions'; // Импортируем хук прав
import { useBranding } from '../context/BrandingContext';
import CaseWorkstation from '../components/CaseWorkstation';

// --- 2. КОМПОНЕНТ: Назначение команды ---
const TeamAssignment = ({ caseData, onAssignmentUpdate, canAssign }) => {
    const [attorneys, setAttorneys] = useState([]);
    const [paralegals, setParalegals] = useState([]);
    const [selectedAttorneyId, setSelectedAttorneyId] = useState('');
    const [selectedParalegalId, setSelectedParalegalId] = useState('');
    const [isLoading, setIsLoading] = useState(false); // Локальная загрузка для списка юристов
    const [isSaving, setIsSaving] = useState(false); // Состояние сохранения

    // Эффект для обновления выбранных ID, когда caseData (из props) меняется
    useEffect(() => {
        setSelectedAttorneyId(caseData?.assigned_attorney_id || '');
        setSelectedParalegalId(caseData?.assigned_paralegal_id || '');
    }, [caseData]); // Зависит от caseData

    // Эффект для загрузки списков сотрудников (только если есть права)
    useEffect(() => {
        if (!canAssign) {
            setIsLoading(false);
            return; // Не грузить, если нет прав
        }

        const fetchTeamMembers = async () => {
            setIsLoading(true);
            try {
                // Загружаем параллельно
                const [attorneysRes, paralegalsRes] = await Promise.all([
                    apiService.getUsersByRole('ATTORNEY'),
                    apiService.getUsersByRole('PARALEGAL')
                ]);
                setAttorneys(attorneysRes || []);
                setParalegals(paralegalsRes || []);
            } catch (error) {
                toast.error("Failed to load team members list.");
                console.error("Error fetching team members:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchTeamMembers();
    }, [canAssign]); // Зависит только от права canAssign

    // Обработчик сохранения
    const handleSaveAssignments = async () => {
        setIsSaving(true);
        const toastId = toast.loading('Saving assignments...');
        try {
            const assignmentData = {
                assigned_attorney_id: selectedAttorneyId ? parseInt(selectedAttorneyId, 10) : null,
                assigned_paralegal_id: selectedParalegalId ? parseInt(selectedParalegalId, 10) : null,
            };
            await apiService.assignTeamToCase(caseData.id, assignmentData);
            toast.success('Team assignments updated!', { id: toastId });
            if (onAssignmentUpdate) {
                onAssignmentUpdate(); // Вызываем колбэк для обновления
            }
        } catch (error) {
            toast.error(`Failed to update assignments: ${error.message}`, { id: toastId });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="card assignment-card">
            <h3 className="card-header">Assign Team</h3>
            {isLoading ? (
                <LoadingSpinner />
            ) : (
                <>
                    <div className="form-group">
                        <label htmlFor="attorney-select">Assigned Attorney</label>
                        <select
                            id="attorney-select"
                            className="form-input"
                            value={selectedAttorneyId}
                            onChange={(e) => setSelectedAttorneyId(e.target.value)}
                            disabled={!canAssign || isSaving} // Блокируем, если нет прав или идет сохранение
                        >
                            <option value="">-- Unassigned --</option>
                            {attorneys.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.first_name} {user.last_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="paralegal-select">Assigned Paralegal</label>
                        <select
                            id="paralegal-select"
                            className="form-input"
                            value={selectedParalegalId}
                            onChange={(e) => setSelectedParalegalId(e.target.value)}
                            disabled={!canAssign || isSaving}
                        >
                            <option value="">-- Unassigned --</option>
                            {paralegals.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.first_name} {user.last_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* Показываем кнопку, только если есть права */}
                    {canAssign && (
                        <div className="form-actions">
                            <button
                                className="btn btn-primary"
                                onClick={handleSaveAssignments}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save Assignments'}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// --- 3. КОМПОНЕНТ: История назначений ---
const AssignmentHistory = ({ history, isLoading, users }) => {

    // (v2.1) ИСПРАВЛЕНИЕ: Хуки useMemo ПЕРЕМЕЩЕНЫ В НАЧАЛО (до return)
    const assignmentEvents = useMemo(() =>
        history?.filter(event => event.event_type === 'team_assigned') || [],
    [history]);

    const userMap = useMemo(() =>
        new Map(users.map(user => [user.id, `${user.first_name || ''} ${user.last_name || ''}`.trim()])),
    [users]);
    // --- КОНЕЦ ИСПРАВЛЕНИЯ ---

    if (isLoading) {
        return (
            <div className="card assignment-history-card">
                <h3 className="card-header">Assignment History</h3>
                <p>Loading history...</p>
            </div>
        );
    }

    if (assignmentEvents.length === 0) {
        return (
            <div className="card assignment-history-card">
                <h3 className="card-header">Assignment History</h3>
                <p className="no-history-message">No assignment history for this case.</p>
            </div>
        );
    }

    // Функция для получения имени пользователя по ID
    const getUserName = (userId) => userMap.get(userId) || `User ID: ${userId}`;

    return (
        <div className="card assignment-history-card">
            <h3 className="card-header">Assignment History</h3>
            <ul className="history-list">
                {/* Показываем последние события первыми */}
                {assignmentEvents.slice().reverse().map(entry => (
                    <li key={entry.id} className="history-item">
                        <div className="history-item-content">
                            <span className="font-semibold">{entry.event_title}</span>
                            <span> by </span>
                            <span className="font-semibold">{getUserName(entry.actor_user_id)}</span>.
                        </div>
                        <div className="history-item-time">
                            {new Date(entry.created_at).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};


// --- 4. КОМПОНЕНТ: Related Tasks ---
const RelatedTasks = ({ caseId }) => {
    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchTasks = async () => {
            setIsLoading(true);
            try {
                const data = await apiService.getTasks({ case_id: caseId });
                setTasks(data || []);
            } catch (err) {
                console.error('Failed to load related tasks:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchTasks();
    }, [caseId]);

    const priorityClass = (p) => ({ URGENT: 'priority-urgent', HIGH: 'priority-high', MEDIUM: 'priority-medium', LOW: 'priority-low' }[p] || '');
    const statusLabel = (s) => ({ TODO: 'To Do', IN_PROGRESS: 'In Progress', REVIEW: 'Review', DONE: 'Done' }[s] || s);

    return (
        <div className="card">
            <h3 className="card-header">Related Tasks ({isLoading ? '…' : tasks.length})</h3>
            {isLoading ? (
                <p>Loading tasks...</p>
            ) : tasks.length === 0 ? (
                <p className="no-history-message">No tasks linked to this case.</p>
            ) : (
                <ul className="history-list">
                    {tasks.map(task => (
                        <li key={task.id} className="history-item">
                            <div className="history-item-content">
                                <span className={`status-badge ${priorityClass(task.priority)}`}>{task.priority}</span>
                                {' '}
                                <span className="font-semibold">{task.title}</span>
                            </div>
                            <div className="history-item-time">
                                {statusLabel(task.status)}
                                {task.due_date ? ` · Due ${new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};


const CASE_STATUSES = [
    { value: 'open',        label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'pending',     label: 'Pending' },
    { value: 'closed',      label: 'Closed' },
];

// --- 5. ОСНОВНОЙ КОМПОНЕНТ СТРАНИЦЫ ---
// ═══════════════════════════════════════════════════════════════════════════
// СРОК ПО ДЕЛУ (quick-260910-jmn)
// ───────────────────────────────────────────────────────────────────────────
// 🔴 ТРИ ПЕРЕКЛЮЧАТЕЛЯ, А НЕ «КНОПКА + СКРЫТАЯ ВЕТКА». Без явного «не
//    спрашивали» состояние необратимо: счётчик дел без срока не может вернуться
//    вверх, а ошибку нечем исправить. Третье состояние достижимо только тогда,
//    когда его можно ВЫБРАТЬ.
// 🔴 ОТКАЗ СЕРВЕРА ПОКАЗЫВАЕТСЯ ДОСЛОВНО. Валидатор двери называет причину
//    («срок одновременно известен и признан неизвестным»); превратив её в «что-то
//    пошло не так», мы отняли бы у человека единственное, что помогает.
// ═══════════════════════════════════════════════════════════════════════════
const SOSTOYANIYA_SROKA = [
    { kluch: 'izvesten', metka: 'известен' },
    { kluch: 'priznan_neizvestnym', metka: 'пока неизвестен' },
    { kluch: 'ne_sprashivali', metka: 'не спрашивали' },
];

const sostoyaniePoDelu = (c) => {
    if (c && c.deadline) return 'izvesten';
    if (c && c.deadline_unknown) return 'priznan_neizvestnym';
    return 'ne_sprashivali';
};

const SrokDela = ({ caseData, onSaved }) => {
    const [sostoyanie, postavitSostoyanie] = useState(sostoyaniePoDelu(caseData));
    const [data, postavitDatu] = useState((caseData && caseData.deadline) || '');
    const [prichina, postavitPrichinu] = useState((caseData && caseData.deadline_reason) || '');
    const [pishem, postavitPishem] = useState(false);

    const sohranit = async () => {
        const payload = sostoyanie === 'izvesten'
            ? { deadline: data || null, deadline_reason: prichina || null, deadline_unknown: false }
            : (sostoyanie === 'priznan_neizvestnym'
                ? { deadline: null, deadline_reason: prichina || null, deadline_unknown: true }
                : { deadline: null, deadline_reason: null, deadline_unknown: false });
        postavitPishem(true);
        try {
            await apiService.setCaseDeadline(caseData.id, payload);
            onSaved();
        } catch (err) {
            toast.error((err && err.message) || 'причина не названа');
        } finally {
            postavitPishem(false);
        }
    };

    return (
        <div className="fk-srok-blok">
            <strong>Срок по делу</strong>
            <div className="fk-srok-vybor">
                {SOSTOYANIYA_SROKA.map((s) => (
                    <label key={s.kluch}>
                        <input type="radio" name="fk-srok" value={s.kluch}
                               checked={sostoyanie === s.kluch}
                               onChange={() => postavitSostoyanie(s.kluch)} />
                        {' '}{s.metka}
                    </label>
                ))}
            </div>
            {sostoyanie === 'izvesten' ? (
                <input className="fk-srok-pole" type="date" value={data}
                       onChange={(e) => postavitDatu(e.target.value)} />
            ) : null}
            {/* Подпись-причина гаснет при «не спрашивали»: причина ЧЕГО? */}
            {sostoyanie === 'ne_sprashivali' ? null : (
                <input className="fk-srok-pole" type="text" value={prichina}
                       placeholder="Подпись-причина"
                       onChange={(e) => postavitPrichinu(e.target.value)} />
            )}
            <button type="button" className="fk-srok-knopka" disabled={pishem}
                    onClick={sohranit}>
                Сохранить
            </button>
            <p className="fk-srok-sostoyanie">
                Сейчас: {SOSTOYANIYA_SROKA.find((s) => s.kluch === sostoyaniePoDelu(caseData)).metka}
                {caseData && caseData.deadline ? ` (${caseData.deadline})` : ''}
            </p>
        </div>
    );
};

const CaseDetailPage = () => {
    const { caseId } = useParams();
    const [caseData, setCaseData] = useState(null);
    const [assignmentHistory, setAssignmentHistory] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [isLoadingCase, setIsLoadingCase] = useState(true);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    // 🔴 РУБЕЖ АРЕНДАТОРА — ПЕРВЫЙ НА ЭТОЙ СТРАНИЦЕ, И ЕГО РЕШЕНИЕ ПО-ПРЕЖНЕМУ
    //    ПРИНИМАЕТ BrandingContext. Колонки срока завелись у всех арендаторов,
    //    но потребитель срока — карточка «Главное сейчас» — есть ТОЛЬКО у
    //    fitenko. Поле, которое у MLG никто не заполнит и никто не прочитает, —
    //    поле, которого нет; заодно экран MLG остаётся нетронутым, а это чужой
    //    продукт.
    const { isFitenko } = useBranding();

    // (v2.1) Получаем хук прав
    const { hasPermission } = usePermissions();
    // Определяем права
    // (ПРЕДПОЛОЖЕНИЕ: hasPermission('...') реализован в usePermissions.js)
    const canAssign = hasPermission('case:assign');
    const canViewHistory = hasPermission('case:view:team') || hasPermission('case:view:all');

    // Загрузка деталей дела
    const fetchCaseDetails = useCallback(async (showLoadingSpinner = true) => {
        if (showLoadingSpinner) setIsLoadingCase(true);
        try {
            const data = await apiService.getCaseById(caseId);
            setCaseData(data);
        } catch (error) {
            toast.error(`Failed to load case details: ${error.message}`);
            setCaseData(null);
        } finally {
            if (showLoadingSpinner) setIsLoadingCase(false);
        }
    }, [caseId]);

    // Загрузка истории
    const fetchAssignmentHistory = useCallback(async () => {
        if (!canViewHistory) { setIsLoadingHistory(false); return; }
        setIsLoadingHistory(true);
        try {
            const historyData = await apiService.getCaseTimeline(caseId, { limit: 50 });
            setAssignmentHistory(historyData || []);
        } catch (error) {
            toast.error(`Failed to load assignment history: ${error.message}`);
        } finally {
            setIsLoadingHistory(false);
        }
    }, [caseId, canViewHistory]);

    // Загрузка ВСЕХ пользователей (нужна для отображения имен в истории)
    const fetchAllUsers = useCallback(async () => {
         if (!canViewHistory) { setIsLoadingUsers(false); return; }
         setIsLoadingUsers(true);
         try {
            const usersData = await apiService.getUsers();
            setAllUsers(usersData || []);
         } catch (error) {
            toast.error("Failed to load user list for history.");
            console.error("Error fetching all users:", error);
         } finally {
             setIsLoadingUsers(false);
         }
    }, [canViewHistory]);

    // Загрузка всех данных при монтировании
    useEffect(() => {
        fetchCaseDetails(true);
        fetchAssignmentHistory();
        fetchAllUsers();
    }, [fetchCaseDetails, fetchAssignmentHistory, fetchAllUsers]);

    const handleStatusChange = async (newStatus) => {
        if (!newStatus || newStatus === caseData?.status) return;
        setIsUpdatingStatus(true);
        const toastId = toast.loading('Updating status...');
        try {
            await apiService.updateCaseStatus(caseId, newStatus);
            toast.success(`Status updated to ${CASE_STATUSES.find(s => s.value === newStatus)?.label}`, { id: toastId });
            fetchCaseDetails(false);
        } catch (err) {
            toast.error(`Failed: ${err.message}`, { id: toastId });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    // Колбэк для обновления данных после сохранения назначений
    const handleAssignmentUpdate = () => {
        fetchCaseDetails(false); // Перезагружаем дело
        fetchAssignmentHistory(); // Перезагружаем историю
    };

    // Мемоизация списка пользователей для истории
    const usersForHistory = useMemo(() => allUsers, [allUsers]);

    // Главный спиннер, пока грузятся дело ИЛИ пользователи (необходимые для рендера)
    if (isLoadingCase || isLoadingUsers) {
        return <LoadingSpinner message="Loading case data..." />;
    }

    if (!caseData) {
        return (
            <div className="container card text-center bg-card text-card-foreground">
                <h2>Case Not Found</h2>
                <p className="text-muted-foreground">The requested case could not be found or you don't have permission to view it.</p>
                <Link to="/cases" className="btn btn-primary bg-primary text-primary-foreground">Back to Cases List</Link>
            </div>
        );
    }
    
    // Форматирование данных для отображения
    const createdDate = new Date(caseData.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const clientName = caseData.owner ? `${caseData.owner.first_name} ${caseData.owner.last_name || ''}` : 'N/A';
    const attorneyName = caseData.assigned_attorney ? `${caseData.assigned_attorney.first_name} ${caseData.assigned_attorney.last_name || ''}` : 'Unassigned';
    const paralegalName = caseData.assigned_paralegal ? `${caseData.assigned_paralegal.first_name} ${caseData.assigned_paralegal.last_name || ''}` : 'Unassigned';


    if (isFitenko) {
        return (
            <CaseWorkstation
                caseData={caseData}
                onBack={() => window.history.back()}
                onAskKatifi={(prompt) => {
                    window.dispatchEvent(new CustomEvent('katifi:open', { detail: { tekst: prompt } }));
                    toast.success('Katifi AI активирован', { icon: '✨' });
                }}
                onUpdateDeadline={() => fetchCaseDetails(false)}
                onAssignTeam={handleAssignmentUpdate}
            />
        );
    }

    // Phase 07.1 Wave 2: page wrapper bg-background; hero card + timeline + assignment
    // cards use bg-card; secondary "Save Assignments" button gets bg-primary via .btn-primary
    // (which is tokenized in CasesPage.css since it's reused via class — search-button shares).
    return (
        <div className="container case-detail-page bg-background text-foreground">
            <header className="page-header">
                <h1 className="page-title text-foreground">Case Details: {caseData.case_number}</h1>
                <p className="page-subtitle text-muted-foreground">{caseData.title}</p>
            </header>

            <div className="case-detail-grid">
                <div className="main-column">
                    {/* Hero/info card surface */}
                    <div className="card bg-card text-card-foreground">
                        <h3 className="card-header">Case Information</h3>
                        <div className="info-grid">
                            <div><strong>Case Number:</strong> {caseData.case_number}</div>
                            <div>
                                <strong>Status:</strong>{' '}
                                {canAssign ? (
                                    <select
                                        className={`status-badge status-${caseData.status?.toLowerCase()} form-input-inline`}
                                        value={caseData.status}
                                        onChange={(e) => handleStatusChange(e.target.value)}
                                        disabled={isUpdatingStatus}
                                        style={{ cursor: 'pointer', fontWeight: 600 }}
                                    >
                                        {CASE_STATUSES.map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <span className={`status-badge status-${caseData.status?.toLowerCase()}`}>{caseData.status}</span>
                                )}
                            </div>
                            <div><strong>Case Type:</strong> {caseData.case_type}</div>
                            <div><strong>Client:</strong> {clientName}</div>
                            <div><strong>Assigned Attorney:</strong> {attorneyName}</div>
                            <div><strong>Assigned Paralegal:</strong> {paralegalName}</div>
                            <div><strong>Created On:</strong> {createdDate}</div>
                        </div>
                        <h4 className="description-header">Description</h4>
                        <p>{caseData.description || "No description provided."}</p>
                    </div>

                    {/* Related Tasks — timeline event items use bg-popover surface */}
                    <div className="bg-popover">
                    <RelatedTasks caseId={caseId} />
                    </div>

                    {/* Компонент Истории Назначений */}
                    {canViewHistory && (
                        <AssignmentHistory
                            history={assignmentHistory}
                            isLoading={isLoadingHistory}
                            users={usersForHistory}
                        />
                    )}
                </div>
                
                <div className="sidebar-column bg-secondary">
                    {/* 🔴 СРОК ИДЁТ ПЕРВЫМ, И ЭТО ВИДНО ТОЛЬКО У FITENKO (quick-260911-0ev).
                        Порядок — решение владельца: срок важнее назначения, а под
                        карточкой назначения он уезжал под сгиб. Перестановка
                        БЕЗОПАСНА для остальных трёх арендаторов (mlg · eln · elfny)
                        ровно потому, что блок стоит внутри ветки `isFitenko`: у них
                        в этой колонке было и остаётся ОДНО дитя — `TeamAssignment`,
                        и порядок из одного элемента не меняется по построению.
                        Файл ОБЩИЙ на четверых, поэтому безопасность здесь названа,
                        а не подразумевается. */}
                    {isFitenko ? (
                        <SrokDela caseData={caseData} onSaved={() => fetchCaseDetails(false)} />
                    ) : null}
                    {/* Компонент Назначения Команды — sidebar uses bg-secondary surface tint */}
                    <TeamAssignment
                        caseData={caseData}
                        onAssignmentUpdate={handleAssignmentUpdate}
                        canAssign={canAssign} // Передаем право на назначение
                    />
                </div>
            </div>
        </div>
    );
};

export default CaseDetailPage;
// --- Конец кода ---